import { createContext, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe } from "lucide-react";
import { LANGS, T } from "./translations";

const LangContext = createContext({
  lang: "hing",
  setLang: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(
    () => localStorage.getItem("jamna_lang") || "hing"
  );

  const setLang = (l) => {
    localStorage.setItem("jamna_lang", l);
    setLangState(l);
  };

  const t = (k) => (T[lang] && T[lang][k]) || T.hing[k] || k;

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);

export function LangSwitcher({ className = "" }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-switcher-toggle"
        aria-expanded={open}
        aria-label="Change language"
        className="flex items-center gap-1.5 border border-line px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-sage transition-colors duration-300 hover:border-harvest hover:text-cream"
      >
        <Globe className="h-3.5 w-3.5 text-harvest" />
        {current.short}
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            data-testid="lang-dropdown"
            className="absolute right-0 top-full z-[70] mt-2 w-36 border border-line bg-moss shadow-xl"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                data-testid={`lang-${l.code}`}
                className={`block w-full px-4 py-2.5 text-left text-xs font-medium transition-colors duration-200 ${
                  lang === l.code
                    ? "bg-harvest/15 text-harvest"
                    : "text-sage hover:bg-forest hover:text-cream"
                }`}
              >
                {l.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
